import { blocksClient } from '@/lib/blocks/client';
import { isDepartmentRole } from '@/lib/department-scope';
import { HRM_PERMISSION_PREFIX } from '@/constant/hr/permissions';
import { IamRole, IamRoleUser, OrganizationRoleRow } from '../types/role.types';

/**
 * Blocks IAM roles, as the Role Manager works with them (see `src/lib/organization-roles.ts` for the
 * three kinds of roles).
 *
 * IAM facts this relies on (checked on dev, 2026-10-25):
 * - A role is project-wide: creating one gives every organization a copy. What it grants is set per
 *   organization (`assign-permissions` with `organizationId`), and people get it per organization.
 * - The copies refuse updates ("Can_Not_Change_Default_role"); a change is made on the default
 *   organization's role with `propagateToOtherOrg`.
 * - Deleting archives the role for good: IAM has no restore, and its key can never be used again
 *   ("Role_Slug_Already_In_Use_Including_Archived_Roles"). So only a role nobody holds is deleted.
 * - Roles keep no tags, so the organization a role belongs to is in its key.
 */

const DEFAULT_ORGANIZATION_ID = 'default';
const PAGE_SIZE = 200;
const ROLE_SLUG_USED = 'Role_Slug_Already_In_Use_Including_Archived_Roles';

/** IAM defaults a new role's object ACL to its creator alone, which blocks everyone else from
 * granting it (`Access_Grant_Not_Allowed`) — so every role is created/updated with this ACL
 * instead, matching who already holds `blocks-iam::iam::mutate-roles`. */
const ROLE_MANAGER_ACCESS = {
  roles: ['admin', 'super_admin', 'workflow-operator'],
  userIds: [],
  permissions: [],
};

interface IamResponse {
  isSuccess?: boolean;
  errors?: Record<string, string> | null;
}

interface ArchiveImpact extends IamResponse {
  affectedUserCount?: number;
  blocked?: boolean;
}

interface IamPermissionRow {
  itemId: string;
  resource: string;
  roles?: string[];
  isArchived?: boolean;
}

/** IAM reports validation failures as HTTP 200 with `isSuccess: false`, which the SDK does not
 * throw on — without this check a rejected call looks like a success. */
const assertIamSuccess = (response: IamResponse | undefined, fallback: string) => {
  if (response?.isSuccess === false) {
    throw new Error(Object.values(response.errors ?? {}).join(', ') || fallback);
  }
};

/** Roles of one organization (the signed-in one when `organizationId` is left out), every page. */
export const listRoles = async (
  filter: { slugs?: string[] } = {},
  organizationId?: string
): Promise<IamRole[]> => {
  const roles: IamRole[] = [];
  for (let page = 0; ; page++) {
    const response = (await blocksClient.iam.roles.list({
      // `search` is required by IAM; blank means no filter.
      filter: { search: '', ...filter },
      ...(organizationId ? { organizationId } : {}),
      page,
      pageSize: PAGE_SIZE,
    })) as { data?: IamRole[] } | undefined;
    const items = response?.data ?? [];
    roles.push(...items);
    if (items.length < PAGE_SIZE) return roles;
  }
};

/** The role to change: the default organization's (its copies refuse changes), else the one this
 * organization sees. */
const findEditableRole = async (slug: string): Promise<IamRole> => {
  const fromDefault = await listRoles({ slugs: [slug] }, DEFAULT_ORGANIZATION_ID).catch(() => []);
  const role =
    fromDefault.find((item) => item.slug === slug) ??
    (await listRoles({ slugs: [slug] })).find((item) => item.slug === slug);
  if (!role) throw new Error('ROLE_NOT_FOUND');
  return role;
};

/** Creates a role, unless one with this key exists ('existing'). A key whose role was deleted can't
 * be used again: that throws `ROLE_KEY_ARCHIVED`. */
export const createRole = async (input: {
  slug: string;
  name: string;
  description: string;
}): Promise<'existing' | 'created'> => {
  if ((await listRoles({ slugs: [input.slug] })).some((role) => role.slug === input.slug)) {
    return 'existing';
  }
  let response: IamResponse | undefined;
  try {
    response = (await blocksClient.iam.roles.create({
      ...input,
      read: ROLE_MANAGER_ACCESS,
      update: ROLE_MANAGER_ACCESS,
      delete: ROLE_MANAGER_ACCESS,
    })) as IamResponse | undefined;
  } catch (error) {
    // IAM answers a used key with HTTP 400; the body names the reason. The role isn't listed, so
    // it was deleted.
    const body = (error as { body?: IamResponse }).body;
    if (body?.errors?.Slug === ROLE_SLUG_USED) throw new Error('ROLE_KEY_ARCHIVED');
    throw error;
  }
  if (response?.errors?.Slug === ROLE_SLUG_USED) throw new Error('ROLE_KEY_ARCHIVED');
  assertIamSuccess(response, 'Role create failed');
  return 'created';
};

/** The workflows' service credential holds this role and no user does, so IAM would let it be
 * deleted (for good), which would leave every workflow without permissions. */
const WORKFLOW_OPERATOR_ROLE = 'workflow-operator';

/** The Role Manager's list: every role of the project, by name. */
export const getOrganizationRoleRows = async (): Promise<OrganizationRoleRow[]> =>
  (await listRoles())
    .map((role) => ({
      role,
      readOnly: isDepartmentRole(role.slug) || role.slug === WORKFLOW_OPERATOR_ROLE,
    }))
    .sort((a, b) => a.role.name.localeCompare(b.role.name));

/** Renames a role (or changes its description) in every organization. */
export const updateRole = async (slug: string, input: { name: string; description: string }) => {
  const role = await findEditableRole(slug);
  const response = (await blocksClient.iam.roles.update({
    itemId: role.itemId,
    name: input.name,
    description: input.description,
    parentRoleSlug: null,
    canCreateOwn: false,
    propagateToOtherOrg: true,
    read: ROLE_MANAGER_ACCESS,
    update: ROLE_MANAGER_ACCESS,
    delete: ROLE_MANAGER_ACCESS,
  })) as IamResponse | undefined;
  assertIamSuccess(response, 'Role update failed');
};

/** Deletes (archives, for good) a role nobody holds; while someone does, nothing changes and the
 * answer says how many hold it. */
export const deleteRole = async (
  slug: string
): Promise<{ deleted: true } | { deleted: false; userCount: number }> => {
  const role = await findEditableRole(slug);
  const path = `/iam/v4/iam/roles/${encodeURIComponent(role.itemId)}`;
  const impact = await blocksClient.http.request<ArchiveImpact>(`${path}/archive-impact`);
  assertIamSuccess(impact, 'Role check failed');
  const userCount = impact.affectedUserCount ?? 0;
  if (userCount > 0 || impact.blocked) return { deleted: false, userCount };
  const response = await blocksClient.http.request<IamResponse | undefined>(path, {
    method: 'DELETE',
  });
  assertIamSuccess(response, 'Role delete failed');
  return { deleted: true };
};

/** The users who hold a role in an organization, every page, by name. IAM's `roles` filter already
 * matches within `organizationIds` (checked on dev, 2026-10-27); the holder check below keeps it so
 * if that ever changes. */
export const listRoleUsers = async (slug: string, organizationId: string) => {
  const users: IamRoleUser[] = [];
  for (let page = 0; ; page++) {
    const response = (await blocksClient.iam.users.list({
      page,
      pageSize: PAGE_SIZE,
      sort: { isDescending: false, property: 'firstName' },
      filter: { roles: [slug], organizationIds: [organizationId] },
    })) as { data?: IamRoleUser[] } | undefined;
    const items = response?.data ?? [];
    users.push(...items.filter((user) => user.roles?.[organizationId]?.includes(slug)));
    if (items.length < PAGE_SIZE) return users;
  }
};

/** The app's (hrm::) permissions of one organization, with the roles that hold each there. */
const listOrganizationPermissions = async (organizationId: string) => {
  const rows: IamPermissionRow[] = [];
  for (let page = 0; ; page++) {
    const response = (await blocksClient.iam.permissions.list({
      // IAM requires `search` and `isBuiltIn` (a "yes"/"no" string); blank means no filter. Search
      // doesn't reliably match on the resource, so the app's permissions are picked below.
      filter: { search: '', isBuiltIn: '', isArchived: false },
      organizationId,
      page,
      pageSize: 500,
    })) as { data?: IamPermissionRow[] } | undefined;
    const items = response?.data ?? [];
    rows.push(...items.filter((row) => row.resource?.startsWith(`${HRM_PERMISSION_PREFIX}::`)));
    if (items.length < 500) return rows;
  }
};

/** The permissions (resources) a role grants in an organization. */
export const getRolePermissions = async (slug: string, organizationId: string) =>
  (await listOrganizationPermissions(organizationId))
    .filter((row) => !row.isArchived && row.roles?.includes(slug))
    .map((row) => row.resource);

/** Makes the role grant exactly `resources` (of the app's permissions) in the organization. */
export const setRolePermissions = async (
  slug: string,
  organizationId: string,
  resources: string[]
) => {
  const rows = (await listOrganizationPermissions(organizationId)).filter((row) => !row.isArchived);
  const wanted = new Set(resources);
  const addPermissions = rows
    .filter((row) => wanted.has(row.resource) && !row.roles?.includes(slug))
    .map((row) => row.itemId);
  const removePermissions = rows
    .filter((row) => !wanted.has(row.resource) && row.roles?.includes(slug))
    .map((row) => row.itemId);
  if (addPermissions.length === 0 && removePermissions.length === 0) return;
  const response = (await blocksClient.iam.roles.assignPermissions({
    slug,
    organizationId,
    addPermissions,
    removePermissions,
  })) as IamResponse | undefined;
  assertIamSuccess(response, 'Role permissions update failed');
};
